<template>
  <BaseModal
    :heading="showContactMessage ? $t('subscription.cancelation_flow.contact_message.heading') : $t('subscription.cancelation_flow.form_heading.we_don_want_to_see_you_go')"
    small
    useForm
    @submit="submit"
    @close="close"
  >
    <BaseSlider :step="currentStep">
      <template #1>
        <Step1 v-model="cancelReasons" />
      </template>
      <template #2>
        <Step2 v-model="cancelReasons" />
      </template>
      <template #3>
        <Step3
          v-model="formData"
          :disallowChurn="isDisallowChurnEnabled"
        />
        <BaseText
          v-if="cancellationFailedReason === 'password'"
          color="error"
          mt
          v-test="'cancel-flow-cancellation-failed-message-password'"
        >
          {{ $t('account.auth.two_factor_settings.errors.password') }}
        </BaseText>
        <BaseText
          v-if="cancellationFailedReason === 'general'"
          color="error"
          mt
          v-test="'cancel-flow-cancellation-failed-message-general'"
        >
          {{ $t('subscription.cancelation_flow.cancellation_failed') }}
        </BaseText>
      </template>
    </BaseSlider>
    <template #footer>
      <BaseButton
        v-if="showContactMessage"
        :routerLink="{ name: 'admin-company' }"
        v-test="'cancel-flow-cancel-update-contact-details'"
      >
        {{ $t('subscription.cancelation_flow.contact_message.update_contact_details') }}
      </BaseButton>
      <template v-else>
        <BaseButton
          color="inverted"
          @click="goBack"
          v-test="'cancel-flow-cancel-back'"
        >
          {{ $t('global.back') }}
        </BaseButton>
        <BaseButton
          submitForm
          :loading="saving"
          v-test="'cancel-flow-cancel-submit'"
        >
          {{ currentStep === 3 ? $t('global.actions.cancel_my_subscription') : $t('global.actions.next') }}
        </BaseButton>
      </template>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { flash } from '@/helpers/ui';
import unleash from '@/unleash';
import { useCancelSubscriptionData } from './data';
import {
  CANCEL_SUBSCRIPTION_MUTATION,
  UPDATE_SUBSCRIPTION_CANCEL_REASONS_MUTATION,
} from './graphql';
import Step1 from './Step1.vue';
import Step2 from './Step2.vue';
import Step3 from './step3/index.vue';
import type { CancelReasons } from './types';

const emit = defineEmits(['close', 'back']);

const currentStep = ref<number>(1);
const { cancelReasonData } = useCancelSubscriptionData();
const cancelReasons = ref<CancelReasons>(cancelReasonData);
const formData = ref<{
  otherSoftware: string;
  explanation: string;
  password: string;
}>({
  otherSoftware: '',
  explanation: '',
  password: '',
});

const cancellationFailedReason = ref<'password' | 'general' | null>(null);

const isDisallowChurnEnabled = computed(() =>
  unleash.isEnabled('DisallowChurn'),
);
const showContactMessage = computed(
  () => isDisallowChurnEnabled.value && currentStep.value === 3,
);

const saving = ref(false);

const submit = () => {
  if (currentStep.value === 1) {
    currentStep.value = 2;
    updateSubscriptionCancelReasons();
  } else if (currentStep.value === 2) {
    currentStep.value = 3;
    updateSubscriptionCancelReasons();
  } else {
    cancelSubscription();
  }
};

const goBack = () => {
  cancellationFailedReason.value = null;

  if (currentStep.value === 1) {
    emit('back');
  } else {
    currentStep.value--;
  }
};

const updateSubscriptionCancelReasons = () => {
  const { mutate } = useMutation(UPDATE_SUBSCRIPTION_CANCEL_REASONS_MUTATION);
  return mutate({
    input: {
      cancelReasons: {
        ...cancelReasons.value,
        talk_to_sales: showContactMessage.value,
      },
    },
  });
};

const { t } = useI18n();

const cancelSubscription = () => {
  saving.value = true;
  cancellationFailedReason.value = null;

  updateSubscriptionCancelReasons()
    .then(() => {
      const { mutate } = useMutation(CANCEL_SUBSCRIPTION_MUTATION);
      return mutate({
        input: formData.value,
      });
    })
    .then((result) => {
      if (result?.data?.cancelSubscription?.errors?.length) {
        if (
          result?.data?.cancelSubscription?.errors.find(
            (error) => error.attribute === 'password',
          )
        ) {
          cancellationFailedReason.value = 'password';
        } else {
          cancellationFailedReason.value = 'general';
        }
        return;
      }
      flash(t('subscription.cancelation_flow.success'));
      emit('close');
    })
    .catch(() => {
      cancellationFailedReason.value = 'general';
    })
    .finally(() => {
      saving.value = false;
    });
};

const close = () => {
  emit('close');
};
</script>
